import * as React from 'react';
import { z } from 'zod';
import { Link, useHistory } from 'react-router-dom';
import { ForbiddenError, subject } from '@casl/ability';
import { useQueryClient } from 'react-query';
import merge from 'lodash/merge';
import uniqBy from 'lodash/uniqBy';
import { useController } from 'react-hook-form';

import { Agency, AgencyGoal } from '__generated-api__';
import api from 'api';
import { useAbility } from 'auth';
import { useMutation } from 'hooks/query';
import Form, { FormProvider, useForm } from 'components/Form/Form';
import InputField from 'components/Form/InputField';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/icon';
import { useToast } from 'my-account/toast';
import { Accordion } from 'components/Accordion';
import { getUserName } from 'my-account/utils/user';
import { SelectTerritory } from 'my-account/components/SelectTerritory';
import { SelectCompany } from 'my-account/components/SelectCompany';
import { SelectAgencyGoal } from 'my-account/components/SelectAgencyGoal';
import { getFullTerritoryName } from 'my-account/utils/territory';

const AgencyUpdateAgencyGoalSchema = z.object({
  id: z.number().positive(),
  bill_to_id: z.string(),
  name: z.string(),
});

const AgencyUpdateSchema = z.object({
  name: z.string().nonempty(),
  territory_id: z.number().nullable().optional(),
  company: z
    .object({
      id: z.number().positive(),
      name: z.string().nullable(),
    })
    .optional()
    .nullable(),
  users: z.array(
    z.object({
      id: z.number(),
      first_name: z.string().optional().nullable(),
      last_name: z.string().optional().nullable(),
      email: z.string().email().optional().nullable(),
      phone: z.string().nullable().optional(),
    })
  ),
  goals: z.array(AgencyUpdateAgencyGoalSchema.extend({ territory_full_name: z.string().nullable().optional() })),
});

const getAgencyInitialValues = (agency: Agency | undefined): z.infer<typeof AgencyUpdateSchema> => {
  if (typeof agency !== 'undefined') {
    return {
      name: agency.name,
      territory_id: agency.territory?.id || null,
      company: agency.company || null,
      users: agency.users || [],
      goals:
        agency.goals?.map((goal) => ({
          ...goal,
          territory_full_name: goal.territory ? getFullTerritoryName(goal.territory) : null,
        })) || [],
    };
  }

  return {
    name: '',
    territory_id: null,
    company: null,
    users: [],
    goals: [],
  };
};

const AgencyRepsField: React.VFC<{ disabled: boolean }> = ({ disabled }) => {
  const arrayField = useController<z.infer<typeof AgencyUpdateSchema>, 'users'>({
    name: 'users',
  });

  return (
    <div className="o-row">
      <div className="o-col-3@md">
        <p className="u-text-xs u-uppercase u-mb-spacer-base-small">Agency representatives</p>
        <p className="c-note">
          This is the list of agency representatives that will be able to access the platform. They have the same
          permissions as Sales Representatives, able to access companies and locations, view orders, and create quote
          requests.
        </p>
      </div>

      <div className="o-col-9@md">
        <div className="o-row o-row--small-gutters">
          <div className="o-col-12">
            {arrayField.field.value.map((user) => (
              <Accordion
                key={user.id}
                title={getUserName(user).name}
                type="card"
                toggleActions={
                  !disabled ? (
                    <div className="u-hidden u-block@sm">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link to={`/users/${user.id}`} className="c-link-cta-basic c-link-cta--small">
                        <span>Edit representative</span>
                      </Link>
                    </div>
                  ) : undefined
                }
              >
                <div className="o-row">
                  <div className="o-col-6@lg">
                    <div className="c-table-wrapper">
                      <table>
                        <tbody>
                          {Boolean(user.phone) && (
                            <tr>
                              <th>T:</th>
                              <td>{user.phone}</td>
                            </tr>
                          )}
                          <tr>
                            <th>E:</th>
                            <td>
                              <a href={`mailto:${user.email}`}>{user.email}</a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Accordion>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const addAgencyGoalSchema = z.object({
  goals: z.array(AgencyUpdateAgencyGoalSchema.passthrough()),
});

const AddGoal: React.VFC<{
  agencyId?: number;
  goalsIds: string[];
  onAdd: (goals: AgencyGoal[]) => void;
  onClose: () => void;
}> = ({ agencyId, goalsIds, onAdd, onClose }) => {
  const { context, formProps } = useForm({
    schema: addAgencyGoalSchema,
    initialValues: {
      goals: undefined,
    },
    onSubmit(values, ctx, event) {
      event?.stopPropagation();
      onAdd(values.goals as unknown as AgencyGoal[]);
      onClose();

      return Promise.resolve();
    },
  });

  return (
    <FormProvider {...context}>
      <div className="c-fetch-card c-fetch-card--colappsable">
        <div className="c-fetch-card__inner">
          <div className="c-fetch-card__input">
            <SelectAgencyGoal
              name="goals"
              elStyle="fill"
              placeholder="Agency Goal"
              // queryParams={{}}
              isMulti
              selectProps={{
                filterOption: (option) => {
                  // hide agency goals that are currently being listed on the screen
                  return goalsIds.indexOf(option.value) === -1;
                },
                isOptionDisabled: (option, options) => {
                  // disable agency goals that are already assigned to another agency

                  if (!option.agency_id) {
                    // not assigned to any agency
                    return false;
                  } else {
                    // assigned to an agency

                    if (agencyId && agencyId === option.agency_id) {
                      // assigned to current agency
                      return false;
                    }

                    // assigned to some other agency
                    return true;
                  }
                },
                onKeyDown: (event) => {
                  if (event.key === 'Enter') {
                    formProps.onSubmit();
                  }
                },
              }}
              getOptionLabel={(goal) => `${goal.name} (${goal.bill_to_id})`}
            />
          </div>

          <div>
            <button
              className="c-button c-button--white"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                formProps.onSubmit();
              }}
            >
              <span>Add</span>
              <Icon name="arrow" className="o-svg-icon o-svg-right" />
            </button>
          </div>
        </div>
        <div className="c-fetch-card__close">
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              onClose();
            }}
          >
            <Icon name="close-thin" className="o-svg-icon o-svg-right" />
          </button>
        </div>
      </div>
    </FormProvider>
  );
};

const AgencyGoalsField: React.VFC<{ agencyId?: number; disabled: boolean }> = ({ agencyId, disabled }) => {
  const arrayField = useController<z.infer<typeof AgencyUpdateSchema>, 'goals'>({
    name: 'goals',
  });
  const [isAddAgencyGoalOpen, setIsAddAgencyGoalOpen] = React.useState(false);

  const onChange = (value: z.infer<typeof AgencyUpdateSchema>['goals']) => {
    arrayField.field.onChange(value);
  };

  const addGoals = (goals: z.infer<typeof AgencyUpdateSchema>['goals']) => {
    onChange(
      uniqBy([...arrayField.field.value, ...goals], 'id').sort((goal1, goal2) =>
        goal1.name === goal2.name ? goal1.id - goal2.id : goal1.name < goal2.name ? -1 : 1
      )
    );
  };

  const removeGoalWithId = (id: number) => {
    onChange(arrayField.field.value.filter((goal) => goal.id !== id));
  };

  return (
    <div className="o-row">
      <div className="o-col-3@md">
        <p className="u-text-xs u-uppercase u-mb-spacer-base-small">Agency Goals</p>
        <p className="c-note">Use the drop-down list to associate the agency to a specific agency goal.</p>
        {!disabled && (
          /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
          <a
            href="#"
            className="c-link-cta-basic u-mt-spacer-base-small u-mb-spacer-base u-text-xs"
            onClick={(event) => {
              event.preventDefault();
              setIsAddAgencyGoalOpen(true);
            }}
          >
            <span>Add Goal</span>
            <Icon name="add" className="o-svg-icon u-text-neutral-300" />
          </a>
        )}
      </div>

      <div className="o-col-9@md">
        {arrayField.field.value.map((goal) => (
          <Accordion
            key={goal.id}
            title={goal.name}
            subtitle={goal.bill_to_id}
            toggleActions={
              !disabled ? (
                <div className="u-hidden u-block@sm">
                  <button
                    className="c-link-cta-basic c-link-cta--small c-link-cta--multiple"
                    onClick={(event) => {
                      event.preventDefault();
                      removeGoalWithId(goal.id);
                    }}
                    type="button"
                  >
                    <span>Remove goal</span>
                  </button>
                </div>
              ) : undefined
            }
            type="card"
          >
            <div className="o-row">
              <div className="o-col-6@lg">
                {/* TODO: is this button used? remove this? */}
                {/* <p className="u-hidden@sm">
                    <button
                      className="c-link-cta-basic c-link-cta--small"
                      onClick={(event) => {
                        event.preventDefault();
                        removeGoalWithId(goal.id);
                      }}
                      type="button"
                    >
                      <span>Remove goal</span>
                    </button>
                  </p> */}

                {!!goal.territory_full_name && (
                  <>
                    <p className="c-accordion__subtitle">Territory:</p>
                    <p>{goal.territory_full_name}</p>
                  </>
                )}
              </div>
            </div>
          </Accordion>
        ))}

        {!disabled && isAddAgencyGoalOpen && (
          <AddGoal
            agencyId={agencyId}
            goalsIds={arrayField.field.value.map((goal) => String(goal.id))}
            onAdd={(goals) => {
              addGoals(
                goals.map((goal) => ({
                  ...goal,
                  territory_full_name: goal.territory ? getFullTerritoryName(goal.territory) : null,
                }))
              );
            }}
            onClose={() => {
              setIsAddAgencyGoalOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export const EditAgencyForm: React.VFC<{ agency?: Agency }> = ({ agency }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();
  // const auth = useAuth();
  const ability = useAbility();
  const [createAgency] = useMutation(api.agency.createAgency);
  const [updateAgency] = useMutation(api.agency.updateAgency);
  const [deleteAgency] = useMutation(api.agency.deleteAgency);
  // const isAdmin = auth.status === AuthStatus.LoggedIn && auth.currentUser.role === AdminUserRoleEnum.Admin;

  const canSubmit = agency ? ability.can('update', subject('Agency', agency)) : ability.can('create', 'Agency');
  const canRemove = agency ? ability.can('delete', subject('Agency', agency)) : false;

  const TerritoryField = ({ disabled }: { disabled: boolean }) => {
    return (
      <div className="o-col-12">
        <SelectTerritory
          name="territory_id"
          label="Territory"
          placeholder="Select a territory..."
          elStyle="fill"
          disabled={disabled}
        />
      </div>
    );
  };

  return (
    <>
      <Form
        schema={AgencyUpdateSchema}
        onSubmit={async (values, ctx) => {
          ForbiddenError.from(ability).throwUnlessCan(
            agency ? 'update' : 'create',
            subject('Agency', merge({}, agency, values))
          );

          const data = {
            ...values,
          };

          if (typeof agency === 'undefined') {
            const res = await createAgency([{ createAgencyBody: { ...data } }]);
            ctx.reset(getAgencyInitialValues(res.data));
            history.push(`/agencies/${res.data.id}`);

            toast.notify({
              type: 'success',
              title: 'Success',
              message: "You've successfully created a new Agency.",
            });
          } else {
            const res = await updateAgency([{ id: agency.id, updateAgencyBody: { ...data } }]);

            await Promise.all([
              queryClient
                .cancelQueries(api.agency.listAgencies.getQueryKey()[0])
                .then(() => queryClient.invalidateQueries(api.agency.listAgencies.getQueryKey()[0])),
              queryClient
                .cancelQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])
                .then(() => queryClient.invalidateQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])),
            ]);

            ctx.reset(getAgencyInitialValues(res.data));

            toast.notify({
              type: 'success',
              title: 'Success',
              message: "You've successfully updated the Agency.",
            });
          }
        }}
        initialValues={getAgencyInitialValues(agency)}
        className="u-mb-0"
        hasFloatingLabels
        formProps={{ mode: 'onSubmit' }}
      >
        <section className="c-block c-block--spacing-t-extra-small c-block--spacing-b c-block--spacing-b-large@md">
          <div className="o-container-fluid">
            <div className="o-row">
              <div className="o-col-3@md">
                <p className="u-text-xs u-uppercase u-mb-spacer-base-small">General info</p>
                <p className="c-note">Enter the agency name into the Agency name field.</p>
              </div>

              <div className="o-col-9@md o-col-6@lg">
                <div className="o-row o-row--small-gutters">
                  <div className="o-col-12">
                    <InputField name="name" label="Agency Name" elStyle="fill" disabled={!canSubmit} />
                  </div>
                  <TerritoryField disabled={!canSubmit} />
                  <div className="o-col-12">
                    <SelectCompany
                      elStyle="fill"
                      name="company"
                      label="Company"
                      selectProps={{ autoComplete: 'off', isClearable: true }}
                      disabled={!canSubmit}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="o-row">
              <div className="o-col-12">
                <hr className="u-mt-spacer-base u-mt-spacer-base-large@sm u-mb-spacer-base-large u-mb-spacer-section-small@sm" />
              </div>
            </div>

            <AgencyRepsField disabled={!canSubmit} />

            <div className="o-row">
              <div className="o-col-12">
                <hr className="u-mt-spacer-base u-mt-spacer-base-large@sm u-mb-spacer-base-large u-mb-spacer-section-small@sm" />
              </div>
            </div>

            <AgencyGoalsField agencyId={agency?.id} disabled={!canSubmit} />

            <div className="o-row">
              <div className="o-col-12">
                <hr className="u-mt-spacer-base u-mt-spacer-base-large@sm u-mb-spacer-base-large u-mb-spacer-section-small@sm" />
              </div>
            </div>

            <div className="o-row u-items-center">
              <div className="o-col-4@md o-offset-3@md">
                <div className="c-form-footer">
                  <SubmitButton variant="secondary" isFull disabled={!canSubmit}>
                    Save changes
                  </SubmitButton>
                </div>
              </div>

              {canRemove && agency && (
                <div className="o-col-5@md u-text-right@md">
                  {agency.status ? (
                    /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                    <a
                      href="#"
                      className="c-link-cta-basic c-link-cta--small u-mt-spacer-base u-mt-0@md u-mb-0"
                      onClick={async (event) => {
                        event.preventDefault();

                        if (window.confirm(`Are you sure that you really want to archive "${agency.name}" agency?`)) {
                          await deleteAgency([{ id: agency.id }]);

                          await Promise.all([
                            queryClient
                              .cancelQueries(api.agency.listAgencies.getQueryKey()[0])
                              .then(() => queryClient.invalidateQueries(api.agency.listAgencies.getQueryKey()[0])),
                            queryClient
                              .cancelQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])
                              .then(() =>
                                queryClient.invalidateQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])
                              ),
                          ]);

                          history.push('/agencies');
                          toast.notify({
                            type: 'success',
                            title: 'Success',
                            message: `The "${agency.name}" agency was archived successfully.`,
                          });
                        }
                      }}
                    >
                      <Icon name="trash" className="o-svg-icon o-svg-larger" />
                      <span>Archive agency</span>
                    </a>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      href="#"
                      className="c-link-cta-basic c-link-cta--small u-mt-spacer-base u-mt-0@md u-mb-0"
                      onClick={async (event) => {
                        event.preventDefault();

                        if (window.confirm(`Are you sure that you really want to activate "${agency.name}" agency?`)) {
                          await updateAgency([
                            {
                              id: agency.id,
                              updateAgencyBody: {
                                name: agency.name,
                                status: 1,
                              },
                            },
                          ]);
                          await Promise.all([
                            queryClient
                              .cancelQueries(api.agency.listAgencies.getQueryKey()[0])
                              .then(() => queryClient.invalidateQueries(api.agency.listAgencies.getQueryKey()[0])),
                            queryClient
                              .cancelQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])
                              .then(() =>
                                queryClient.invalidateQueries(api.agency.getAgency.getQueryKey({ id: agency.id })[0])
                              ),
                          ]);
                          toast.notify({
                            type: 'success',
                            title: 'Success',
                            message: `The "${agency.name}" agency was activated successfully.`,
                          });
                        }
                      }}
                    >
                      <Icon name="export" className="o-svg-icon o-svg-larger" />
                      <span>Activate agency</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </Form>
    </>
  );
};
